import React, { useEffect, useState, useRef, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleArrowDown, faHandshake, faCommentDollar, faStore,
  faPlugCirclePlus, faDashboard, faExplosion, faWandSparkles, faUser,
} from '@fortawesome/free-solid-svg-icons'
import { faStripe } from '@fortawesome/free-brands-svg-icons'
import axios from 'axios'
import DatePicker from 'react-datepicker'
import { DateTime } from 'luxon'
import Swal from 'sweetalert2'
import jwt_decode from 'jwt-decode'
import AuthContext from '../../middlewares/AuthContext'
import { useTranslation } from 'react-i18next'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders : () => Promise<Record<string, string>>
  token      : string | null
}

interface DecodedToken {
  roles: { name: string }[]
}

interface ReportItem {
  title   : string
  subtitle: string
  link    : string
  faicon  : any
  color   : string
}

const MAX_REPORTS = 12
const yearAgo     = () => DateTime.now().minus({ years: 1 }).toJSDate()

const buildReports = (role: string, t: (k: string) => string): ReportItem[] => {
  const sub = t('reports.get_report')

  const ADMIN: ReportItem[] = [
    { title: t('reports.payments'),    subtitle: sub, link: 'reports/customers',          faicon: faUser,          color: BRAND },
    { title: t('reports.pospayment'),  subtitle: sub, link: 'reports/partners',           faicon: faHandshake,     color: '#3b82f6' },
    { title: t('reports.marketing'),   subtitle: sub, link: 'reports/marketings',         faicon: faCommentDollar, color: '#f59e0b' },
    { title: t('reports.store'),       subtitle: sub, link: 'reports/stores',             faicon: faStore,         color: '#8b5cf6' },
    { title: t('reports.suscription'), subtitle: sub, link: 'reports/licenses',           faicon: faStripe,        color: '#635bff' },
    { title: 'Subscription to Expire', subtitle: sub, link: 'reports/to-expire',          faicon: faExplosion,     color: '#ef4444' },
    { title: t('reports.renovated'),   subtitle: sub, link: 'reports/licenses/renovated', faicon: faPlugCirclePlus,color: '#10b981' },
    { title: t('reports.dashboard'),   subtitle: sub, link: 'reports/dashboard',          faicon: faDashboard,     color: '#06b6d4' },
    { title: 'Primera activación',     subtitle: sub, link: 'reports/first-activation',   faicon: faWandSparkles,  color: '#f97316' },
    { title: 'Uso diario de apps',     subtitle: sub, link: 'reports/average',            faicon: faWandSparkles,  color: '#64748b' },
  ]

  const PARTNER: ReportItem[] = [
    { title: t('reports.pospayment'),  subtitle: sub, link: 'reports/partner/partners',   faicon: faHandshake,     color: '#3b82f6' },
    { title: t('reports.suscription'), subtitle: sub, link: 'reports/partner/licenses',   faicon: faStripe,        color: '#635bff' },
    { title: 'Subscription to Expire', subtitle: sub, link: 'reports/to-expire',          faicon: faExplosion,     color: '#ef4444' },
    { title: t('reports.renovated'),   subtitle: sub, link: 'reports/licenses/renovated', faicon: faPlugCirclePlus,color: '#10b981' },
    { title: t('reports.dashboard'),   subtitle: sub, link: 'reports/dashboard',          faicon: faDashboard,     color: '#06b6d4' },
  ]

  const SUBPARTNER: ReportItem[] = [
    { title: t('reports.suscription'), subtitle: sub, link: 'reports/partner/licenses',   faicon: faStripe,        color: '#635bff' },
    { title: 'Subscription to Expire', subtitle: sub, link: 'reports/to-expire',          faicon: faExplosion,     color: '#ef4444' },
    { title: t('reports.dashboard'),   subtitle: sub, link: 'reports/dashboard',          faicon: faDashboard,     color: '#06b6d4' },
  ]

  if (role === 'admin')      return ADMIN
  if (role === 'partner')    return PARTNER
  if (role === 'subpartner') return SUBPARTNER
  return []
}

const Reports: React.FC = () => {
  const { getHeaders, token } = useContext(AuthContext) as AuthCtx
  const { t, i18n }           = useTranslation()

  const decoded    = token ? jwt_decode<DecodedToken>(token) : null
  const role       = decoded?.roles?.[0]?.name ?? ''

  const downloadTriggerRef = useRef<HTMLButtonElement>(null)
  const downloadCloseRef   = useRef<HTMLButtonElement>(null)

  const [reports, setReports]     = useState<ReportItem[]>([])
  const [loading, setLoading]     = useState(false)
  const [startDates, setStartDates] = useState<Date[]>(
    Array.from({ length: MAX_REPORTS }, yearAgo)
  )
  const [endDates, setEndDates] = useState<Date[]>(
    Array.from({ length: MAX_REPORTS }, () => new Date())
  )

  useEffect(() => {
    setReports(buildReports(role, t))
  }, [i18n.language])

  const updateDate = (
    setter : React.Dispatch<React.SetStateAction<Date[]>>,
    index  : number,
    value  : Date | null
  ) => {
    if (!value) return
    setter(prev => prev.map((d, j) => (j === index ? value : d)))
  }

  const getReport = async (e: React.FormEvent, item: ReportItem, i: number) => {
    e.preventDefault()
    const startDt = DateTime.fromJSDate(startDates[i])
    const endDt   = DateTime.fromJSDate(endDates[i])

    if (!startDt.isValid || !endDt.isValid) {
      Swal.fire({ icon: 'error', title: 'Invalid date range' })
      return
    }

    const diff = endDt.diff(startDt, ['months', 'days'])
    if (diff.months === 12 && diff.days > 0) {
      Swal.fire({ icon: 'info', title: 'You can only get a report within one year' })
      return
    }

    const apiUrl = item.link === 'reports/to-expire'
      ? `${url}${item.link}`
      : `${url}${item.link}/${startDt.toISODate()}/${endDt.toISODate()}`

    setLoading(true)
    downloadTriggerRef.current?.click()

    try {
      const headers = await getHeaders()
      const res     = await axios.get(apiUrl, { headers })
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]))
      const a       = document.createElement('a')
      a.href        = blobUrl
      a.download    = `${item.link.replace(/\//g, '-')}-${startDt.toISODate()}-${endDt.toISODate()}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(blobUrl)
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
      setTimeout(() => downloadCloseRef.current?.click(), 500)
    }
  }

  return (
    <>
      {/* ── Hidden modal trigger / close ─────────────────────────────── */}
      <button
        type="button"
        className="d-none"
        data-bs-toggle="modal"
        data-bs-target="#downloadModal"
        ref={downloadTriggerRef}
      />

      {/* ── Download progress modal ───────────────────────────────────── */}
      <div
        className="modal fade"
        id="downloadModal"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content" style={{ borderRadius: 16, border: 'none', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <button
              type="button"
              className="d-none"
              data-bs-dismiss="modal"
              aria-label="Close"
              ref={downloadCloseRef}
            />
            <div className="modal-body text-center py-4">
              <div style={{
                width           : 56,
                height          : 56,
                borderRadius    : '50%',
                backgroundColor : `${BRAND}18`,
                display         : 'flex',
                alignItems      : 'center',
                justifyContent  : 'center',
                margin          : '0 auto 1rem',
              }}>
                <div className="spinner-border" role="status" style={{ color: BRAND, width: 28, height: 28, borderWidth: 3 }}>
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
              <p style={{ margin: 0, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                Generating report…
              </p>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>
                This may take a few seconds
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Page ─────────────────────────────────────────────────────── */}
      <div className="pb-5">

        {/* Page title */}
        <div className="mb-4">
          <h2 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#475569', margin: 0 }}>
            {t('reports.reports')}
          </h2>
        </div>

        {/* Report cards grid */}
        <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4">
          {reports.map((item, i) => (
            <div className="col animate__animated animate__fadeIn" key={`report-${i}`}>
              <div style={{
                backgroundColor : '#fff',
                borderRadius    : 16,
                boxShadow       : '0 4px 20px rgba(0,0,0,0.07)',
                border          : '1px solid #f1f5f9',
                overflow        : 'hidden',
                height          : '100%',
                display         : 'flex',
                flexDirection   : 'column',
              }}>
                {/* Color accent bar */}
                <div style={{ height: 4, backgroundColor: item.color }} />

                {/* Card header */}
                <div style={{ padding: '1.1rem 1.25rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width           : 40,
                    height          : 40,
                    borderRadius    : 10,
                    backgroundColor : `${item.color}18`,
                    display         : 'flex',
                    alignItems      : 'center',
                    justifyContent  : 'center',
                    flexShrink      : 0,
                  }}>
                    <FontAwesomeIcon icon={item.faicon} style={{ color: item.color, fontSize: '1.1rem' }} />
                  </div>
                  <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.04em', lineHeight: 1.3 }}>
                    {item.title}
                  </p>
                </div>

                {/* Form */}
                <form
                  onSubmit={e => getReport(e, item, i)}
                  style={{ padding: '0.5rem 1.25rem 1.25rem', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'flex-end', gap: '0.75rem' }}
                >
                  {item.link !== 'reports/to-expire' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {/* Start date */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {t('reports.start_at')}
                        </label>
                        <DatePicker
                          portalId={`portal-start-${i}`}
                          dateFormat="yyyy-MM-dd"
                          selected={startDates[i]}
                          onChange={val => updateDate(setStartDates, i, val)}
                          className="form-control form-control-sm"
                          id={`startAt-${i}`}
                          name={`startAt-${i}`}
                        />
                      </div>
                      {/* End date */}
                      <div>
                        <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {t('reports.end_at')}
                        </label>
                        <DatePicker
                          portalId={`portal-end-${i}`}
                          dateFormat="yyyy-MM-dd"
                          selected={endDates[i]}
                          onChange={val => updateDate(setEndDates, i, val)}
                          className="form-control form-control-sm"
                          id={`endAt-${i}`}
                          name={`endAt-${i}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Download button */}
                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      display        : 'inline-flex',
                      alignItems     : 'center',
                      justifyContent : 'center',
                      gap            : '0.45rem',
                      width          : '100%',
                      padding        : '0.5rem 1rem',
                      borderRadius   : 9999,
                      backgroundColor: loading ? '#94a3b8' : item.color,
                      color          : '#fff',
                      fontWeight     : 700,
                      fontSize       : '0.82rem',
                      border         : 'none',
                      cursor         : loading ? 'not-allowed' : 'pointer',
                      boxShadow      : loading ? 'none' : `0 4px 12px ${item.color}44`,
                      transition     : 'filter 0.15s, transform 0.15s',
                    }}
                    onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                  >
                    <FontAwesomeIcon icon={faCircleArrowDown} />
                    <span>{item.subtitle}</span>
                  </button>
                </form>

              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}

export default Reports
